import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/Card';
import { EmptyState, NoDataIcon } from '@/components/ui/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { StrategyBadge } from '@/components/ui/StrategyBadge';
import { SchwabLink } from '@/components/SchwabLink';
import { schwabResearchUrl } from '@/lib/schwabLinks';
import { PositionAiPanel } from './PositionAiPanel';
import { PayoffBar } from './PayoffBar';
import { expectedMove, oddsFromLegs, shortLegQuotes, type LegBatchResponse } from '@/lib/odds';
import { ExplainDialog } from '@/components/learn/ExplainDialog';
import { buildLearnContext, finiteOrNull, perSharePremium, type LearnContext } from '@/learn';
import {
  analyzeShortStrikeSafety,
  type PositionSafety,
  type SideSafety,
} from '@/lib/positionSafety';
import {
  toLocalDate,
  getDaysToExpiration,
  formatExpirationDate,
  formatCurrency,
} from '@/lib/formatters';
import { cn, groupBy } from '@/lib/utils';
import { consolidateLegs } from '@/lib/consolidateLegs';
import { capitalHeld, heldForSides, detectStrategy } from '@steward/shared';
import type {
  TradeChainWithPositions,
  PositionWithLegs,
  PositionLeg,
  StrategyType,
  CapitalHeld,
  HeldKind,
  HeldSide,
} from '@steward/shared';

export interface RiskViewProps {
  chains: TradeChainWithPositions[] | undefined;
  quotes: Record<string, { price: number; change: number; changePercent: number }> | undefined;
  /**
   * The board's Greeks, or null while the container's query is pending. Required, never
   * defaulted: a forgotten prop must be a type error, not a board that silently never shows odds.
   */
  greeks: LegBatchResponse | null;
}

export interface RiskRow {
  /**
   * `${chainId}|${expKey}` — unique per row now that one chain can produce more than one row (a
   * chain whose sides sit at two expirations). The card list key, the Explain lookup and
   * `attachGreeks`'s Greeks-group matching all key off this, never off `chainId` alone.
   */
  rowKey: string;
  chainId: string;
  positionId: string;
  symbol: string;
  strategyType: StrategyType;
  /**
   * The label `detectStrategy` (`@steward/shared`) reads off THIS row's own consolidated legs —
   * "Put Credit Spread" / "Call Credit Spread" for a one-sided row, "Iron Condor" for a row whose
   * sides share a date, "Short Put" / "Short Call" for a lone short. Independent of
   * `strategyType`, which is the CHAIN's label and stays "Iron Condor" even after a one-side
   * roll splits the chain into two one-sided rows — the card shows this field instead of
   * `strategyType` whenever the two disagree (see `RiskCard`).
   */
  rowStrategyLabel: string;
  netPremium: number;
  currentPrice: number | null;
  changePercent: number | null;
  safety: PositionSafety;
  expiration: Date;
  expKey: string;
  dte: number;
  /** Derived from this row's merged OPEN legs, never from the chain's strategy label. */
  held: CapitalHeld;
  /** How many times the CHAIN has rolled: the highest roll number across its positions. */
  rollCount: number;
  /** This row's merged open SHORT legs, as `POST /quotes/options/legs/batch` wants them. */
  shortLegs: Array<{ optionType: 'call' | 'put'; strikePrice: number; expirationDate: string }>;
  /** Whole-percent chance each short finishes in the money. Null until the board's Greeks land. */
  odds: { putProb: number | null; callProb: number | null } | null;
  /** Expected move to expiration in dollars. Null until Greeks land, or when no IV was quoted. */
  expectedMove: number | null;
  /** The numbers the Explain dialog reads. Built here, where the open legs already are. */
  learnCtx: LearnContext;
}

const kindLabels: Record<HeldKind, string> = {
  put_spread: 'put spread',
  call_spread: 'call spread',
  cash_secured_put: 'cash-secured put',
  uncovered_call: 'uncovered call',
  debit: 'debit',
};

function kindLabel(kinds: HeldKind[]): string {
  return kinds.map((k) => kindLabels[k]).join(' + ');
}

/**
 * What frees up when this expiration passes, and whether the group's arithmetic needs explaining.
 *
 * `heldForSides` is applied PER CHAIN and the results added, exactly as the API's own
 * per-expiration subtotal does (`services/capital/capital-report.ts`) — two different chains'
 * spreads at one date are separate requirements and must add, while one chain's put and call
 * spreads collapse to the wider side (only one can finish in the money). Rows are per chain PER
 * EXPIRATION now (`buildRiskRows` groups every open position's open legs by expiration day, so a
 * chain whose sides sit at two dates gets two rows), and each row's `held.sides` only ever
 * carries its own single date — so for any one `expKey`, at most one of a chain's rows
 * contributes here, and the `byChain` grouping below still collapses to that one row's sides: an
 * iron condor whose two sides share one date already arrives as one row's `held.sides`, and is
 * counted once on the wider side the same way an iron condor held in a single position always was.
 *
 * `rows` is EVERY row on the board, not the group's own rows, purely for symmetry with the
 * board's `groups` computation above it — every side a row carries is now at the row's own
 * `expKey`, so this no longer needs to reach past the group's own rows to find a later side.
 *
 * `condor` reports that some chain holds both a put spread and a call spread at this date — the
 * one case where the rows below deliberately do not add up to the header.
 */
function freeingUp(rows: RiskRow[], expKey: string): { held: number; condor: boolean } {
  const byChain = new Map<string, HeldSide[]>();
  for (const row of rows) {
    const sides = row.held.sides.filter((side) => side.expiration === expKey);
    if (sides.length === 0) continue;
    byChain.set(row.chainId, [...(byChain.get(row.chainId) ?? []), ...sides]);
  }
  let held = 0;
  let condor = false;
  for (const sides of byChain.values()) {
    held += heldForSides(sides);
    if (sides.some((s) => s.kind === 'put_spread') && sides.some((s) => s.kind === 'call_spread')) {
      condor = true;
    }
  }
  return { held, condor };
}

/**
 * The day label a leg's expiration belongs to: the UTC day of the stored UTC-midnight instant,
 * which is exactly what the API sends (`expirationDate.toISOString().slice(0, 10)` in
 * `services/capital/capital-report.ts`). Re-serialising a local-midnight Date instead is one day
 * early east of UTC, and would make the group key disagree with the side keys it groups on.
 */
const dayKey = (expirationDate: Date) => expirationDate.toISOString().slice(0, 10);

function formatStrike(strike: number): string {
  // Strip trailing zeros: 95 -> "95", 102.5 -> "102.5"
  return String(parseFloat(strike.toFixed(2)));
}

function formatSidePercent(percent: number): string {
  return `${percent.toFixed(1)}%`;
}

function sideTextClass(side: SideSafety | null): string {
  if (!side || side.level === null || side.percent === null) return 'text-gray-400';
  if (side.percent < 0) return 'text-red-700 font-medium';
  if (side.level === 'safe') return 'text-green-600';
  if (side.level === 'watch') return 'text-amber-600';
  return 'text-red-600';
}

const overallPillClasses: Record<PositionSafety['overall'], string> = {
  safe: 'bg-green-50 text-green-700',
  watch: 'bg-amber-50 text-amber-700',
  danger: 'bg-red-50 text-red-700',
  'no-quote': 'bg-gray-100 text-gray-500',
  'no-shorts': 'bg-gray-100 text-gray-500',
};

const overallLabels: Record<PositionSafety['overall'], string> = {
  safe: 'Safe',
  watch: 'Watch',
  danger: 'Danger',
  'no-quote': 'no quote',
  'no-shorts': 'long only',
};

/**
 * One row per open or partial chain PER EXPIRATION: the open legs of every one of the chain's
 * OPEN positions (never a closed one) are grouped by the UTC day their expiration falls on
 * (`dayKey`), and each group becomes its own row. Sides merge only when they share a date — the
 * NFLX shape, a put spread on one position and a call spread on another rolled to the SAME
 * expiration, still reads as one iron condor — while a chain whose sides sit at two dates (a
 * one-side roll that left the other side further out, the MA shape) gets two rows, each drawing
 * its own one-sided payoff under its own date header rather than one profile that is wrong for
 * the side it excludes.
 *
 * `positionId` for a row (and so the dialogs it keys — Explain, Ask AI, the card's own `key`
 * via `rowKey`) is, among the positions that contributed a leg to THAT row's group, the one with
 * the highest `rollNumber`, tie -> last one in array order. `netPremium` sums those same
 * positions' own premium — a position whose legs span two dates contributes its whole premium to
 * BOTH of the rows it lands in; that is a deliberate double count, not a bug, since splitting it
 * proportionally by leg count would invent a number neither side actually collected. `rollCount`
 * still looks at ALL of the chain's positions (open or not), unchanged from before, since it
 * describes the chain's whole roll history rather than one row's.
 *
 * Three known limits. The Explain dialog's per-share credit (`perSharePremium`) divides a row's
 * premium by the LARGEST leg count in ITS OWN group, so it is exact only when both sides of a
 * same-date row are the same size — a 5-lot put spread beside a 1-lot call spread reads a
 * blended figure. A position whose legs split across two dates makes this worse, not just
 * differently exact: its WHOLE premium (never a share of it — see `netPremium` above) is divided
 * by only ONE date's legs on each of the two rows it lands in, so the Explain credit reads
 * overstated on BOTH rows, not correct on one and wrong on the other. This is a known limit, not
 * sized against real data, carried over from the one-row-per-chain version. And Ask AI still
 * analyses `positionId` alone, one side of a row the board may draw as an iron condor.
 */
export function buildRiskRows(
  chains: TradeChainWithPositions[] | undefined,
  quotes: RiskViewProps['quotes'],
): RiskRow[] {
  if (!chains) return [];
  const result: RiskRow[] = [];

  for (const chain of chains) {
    if (chain.status !== 'open' && chain.status !== 'partial') continue;
    const symbol = chain.underlyingSymbol;
    const quote = quotes?.[symbol];
    const currentPrice = finiteOrNull(quote?.price);
    const rollCount = Math.max(0, ...chain.positions.map((p) => p.rollNumber));

    // 'partial' positions still carry live short legs — the risk board must show them; a closed
    // position, or one with nothing left open, contributes no legs to any row.
    const openPositions = chain.positions.filter(
      (position) =>
        position.status !== 'closed' && position.legs.some((leg) => leg.quantityRemaining > 0),
    );
    if (openPositions.length === 0) continue;

    // Group every open position's open legs by the UTC day their expiration falls on. A position
    // whose own legs span two dates contributes to two different groups — see the docstring on
    // `netPremium`.
    const groups = new Map<string, { legs: PositionLeg[]; positions: PositionWithLegs[] }>();
    for (const position of openPositions) {
      const openLegs = position.legs.filter((leg) => leg.quantityRemaining > 0);
      for (const leg of openLegs) {
        const day = dayKey(leg.expirationDate);
        let group = groups.get(day);
        if (!group) {
          group = { legs: [], positions: [] };
          groups.set(day, group);
        }
        group.legs.push(leg);
        if (!group.positions.includes(position)) group.positions.push(position);
      }
    }

    for (const day of [...groups.keys()].sort()) {
      const { legs: groupLegs, positions: groupPositions } = groups.get(day)!;

      // The dialogs key off the most recently opened of the positions that landed a leg in THIS
      // group: highest rollNumber, and `>=` (rather than `>`) means a tie hands it to the later
      // one in array order.
      const latestPosition = groupPositions.reduce((latest, position) =>
        position.rollNumber >= latest.rollNumber ? position : latest,
      );

      const netPremium = groupPositions.reduce((sum, position) => sum + position.netPremium, 0);

      // `expiration` is a local-midnight Date for display (formatExpirationDate / DtePill); every
      // leg in this group shares the same UTC day, so any one of them gives the right instant.
      const expiration = toLocalDate(groupLegs[0].expirationDate);
      const expKey = day;

      const safety = analyzeShortStrikeSafety(groupLegs, currentPrice);

      // Shorts only: nothing reads a long leg's delta, and every leg asked for is a chain walk on
      // the other end. `dayKey` again, so the request carries the same day label the API stores.
      const shortLegs = groupLegs
        .filter((leg) => leg.action === 'STO' || leg.action === 'STC')
        .map((leg) => ({
          optionType: leg.optionType,
          strikePrice: leg.strikePrice,
          expirationDate: dayKey(leg.expirationDate),
        }));

      // Named off THIS row's own legs, consolidated first so a leg filled in several lots counts
      // once, not once per fill — `detectStrategy` pattern-matches on `legs.length`.
      const rowStrategyLabel = detectStrategy(
        consolidateLegs(groupLegs).map((leg) => ({
          side: (leg.action === 'STO' || leg.action === 'STC' ? 'short' : 'long') as 'short' | 'long',
          optionType: leg.optionType,
          strike: leg.strikePrice,
          expiration: expKey,
          quantity: leg.quantity,
          pricePerContract: leg.pricePerContract,
          // Unused by `detectStrategy` (it reads only side/optionType/leg count) — 0 is a valid
          // placeholder, never read.
          dollarAmount: 0,
        })),
      );

      result.push({
        rowKey: `${chain.id}|${expKey}`,
        chainId: chain.id,
        positionId: latestPosition.id,
        symbol,
        strategyType: chain.strategyType,
        rowStrategyLabel,
        netPremium,
        currentPrice,
        changePercent: quote?.changePercent ?? null,
        safety,
        expiration,
        expKey,
        dte: getDaysToExpiration(expiration),
        rollCount,
        shortLegs,
        // Populated by `attachGreeks` when the board's batch lands; the row renders without them.
        odds: null,
        expectedMove: null,
        learnCtx: buildLearnContext({
          symbol,
          // PositionLeg already satisfies LearnLegInput; only this group's open legs count.
          legs: groupLegs,
          price: currentPrice,
          netPremiumPerShare: perSharePremium(netPremium, groupLegs),
        }),
        held: capitalHeld(
          groupLegs.map((leg) => ({
            optionType: leg.optionType,
            action: leg.action,
            strikePrice: leg.strikePrice,
            // The same day label the group key uses, so a header can find its own sides.
            expiration: dayKey(leg.expirationDate),
            quantityRemaining: leg.quantityRemaining,
            pricePerContract: leg.pricePerContract,
          }))
        ),
      });
    }
  }

  return result;
}

/**
 * The board's second stage: fold the batched Greeks into rows that already rendered.
 *
 * Pure and total, so it is unit-tested without a query cache. Three outcomes, and they are
 * deliberately different: no response yet -> `odds` stays null and the bar shows no chips at all;
 * a group that errored -> `odds` is present with null sides, so the chips read em dashes and say
 * "we asked and could not price it"; a quoted group -> real numbers.
 *
 * The Explain context is rebuilt by overriding its two delta fields rather than re-running
 * `buildLearnContext`: every other field of a `LearnContext` is independent of delta, and
 * re-deriving them would mean carrying the legs on the row a second time.
 *
 * Groups are matched back to rows by `rowKey`, never `chainId`: a chain whose sides sit at two
 * expirations now produces two rows, and matching on `chainId` alone would hand BOTH of them the
 * same group — the wrong side's odds on one of the two, silently.
 */
export function attachGreeks(rows: RiskRow[], greeks: LegBatchResponse | null): RiskRow[] {
  if (!greeks) return rows;
  const byId = new Map(greeks.groups.map((group) => [group.id, group]));

  return rows.map((row) => {
    const group = byId.get(row.rowKey);
    if (!group) return row;

    const strikes = {
      shortPut: row.safety.put?.shortStrike ?? null,
      shortCall: row.safety.call?.shortStrike ?? null,
    };
    const matched = shortLegQuotes(strikes, group.legs);
    const odds = oddsFromLegs(strikes, group.legs);

    return {
      ...row,
      odds,
      expectedMove: expectedMove(
        row.currentPrice,
        [matched.put?.impliedVolatility ?? null, matched.call?.impliedVolatility ?? null],
        row.dte,
      ),
      learnCtx: {
        ...row.learnCtx,
        // Magnitudes, matching what `shortLegDeltas` already reports to the delta topic.
        shortPutDelta: matched.put?.delta == null ? null : Math.abs(matched.put.delta),
        shortCallDelta: matched.call?.delta == null ? null : Math.abs(matched.call.delta),
      },
    };
  });
}

export function RiskView({ chains, quotes, greeks }: RiskViewProps) {
  const [aiPositionId, setAiPositionId] = useState<string | null>(null);
  // Keyed by rowKey, not positionId: a position whose legs span two expirations now lands a leg
  // in two rows, so a positionId alone cannot say which of the two the click came from.
  // `onExplain` hands back the clicked row's `rowKey` directly (see `RiskCard`) — the dialog
  // itself still opens keyed by position (`explainRow.positionId` below), since `ExplainDialog`
  // resets its AI state per position, not per row.
  const [explainRowKey, setExplainRowKey] = useState<string | null>(null);
  const baseRows = useMemo<RiskRow[]>(() => buildRiskRows(chains, quotes), [chains, quotes]);
  const rows = useMemo<RiskRow[]>(() => attachGreeks(baseRows, greeks), [baseRows, greeks]);

  const groups = useMemo(() => {
    const grouped = groupBy(rows, (row) => row.expKey);
    // Every date capital is held at, not just the dates rows are filed under. In practice every
    // row's held now carries only its own date (rows are per chain per expiration), so this union
    // is already a subset of `Object.keys(grouped)` — kept for symmetry with `freeingUp`, which
    // reads every row's sides rather than just the group's own.
    const expKeys = [
      ...new Set([
        ...Object.keys(grouped),
        ...rows.flatMap((row) => row.held.sides.map((side) => side.expiration)),
      ]),
    ];
    return expKeys
      .map((expKey) => {
        const groupRows = grouped[expKey] ?? [];
        // Built from the key, so a row-less group dates and counts down exactly like any other.
        const expiration = toLocalDate(new Date(`${expKey}T00:00:00.000Z`));
        return {
          expKey,
          dte: getDaysToExpiration(expiration),
          expiration,
          ...freeingUp(rows, expKey),
          rows: [...groupRows].sort((a, b) => {
            const aPct = a.safety.minPercent ?? Infinity;
            const bPct = b.safety.minPercent ?? Infinity;
            if (aPct !== bPct) return aPct - bPct;
            return a.symbol.localeCompare(b.symbol);
          }),
        };
      })
      .sort((a, b) => a.expKey.localeCompare(b.expKey));
  }, [rows]);

  // Looked up rather than stored, so the dialog reads the same freshly-quoted row the card does.
  const explainRow = rows.find((row) => row.rowKey === explainRowKey) ?? null;

  if (rows.length === 0) {
    return (
      <Card>
        <CardContent>
          <EmptyState icon={<NoDataIcon />} title="No open option positions" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {groups.map((group) => (
        <div key={group.expKey}>
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-sm font-semibold text-gray-900">
              {formatExpirationDate(group.expiration)}
            </h3>
            <DtePill dte={group.dte} />
            <span data-testid="group-held" className="text-xs text-gray-500">
              {formatCurrency(group.held, { cents: false })} frees up
            </span>
            {/* Some chain here holds both sides, so the cards below add up to more than this. */}
            {group.condor && (
              <span data-testid="group-held-hint" className="text-xs text-gray-400">
                (iron condor: wider side)
              </span>
            )}
          </div>
          <div className="space-y-2">
            {group.rows.map((row) => (
              <RiskCard
                key={row.rowKey}
                row={row}
                onAskAi={setAiPositionId}
                onExplain={setExplainRowKey}
              />
            ))}
          </div>
        </div>
      ))}
      {/* Keyed by position id, as ExplainDialog requires, so its AI state resets per position. */}
      {explainRow && (
        <ExplainDialog
          key={explainRow.positionId}
          open
          onClose={() => setExplainRowKey(null)}
          positionId={explainRow.positionId}
          ctx={explainRow.learnCtx}
          chain={{ rollCount: explainRow.rollCount }}
        />
      )}
      <Modal
        isOpen={aiPositionId !== null}
        onClose={() => setAiPositionId(null)}
        title="AI Analysis"
        size="lg"
      >
        {aiPositionId !== null && <PositionAiPanel positionId={aiPositionId} key={aiPositionId} />}
      </Modal>
    </div>
  );
}

function DtePill({ dte }: { dte: number }) {
  if (dte < 0) {
    return (
      <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">Expired</span>
    );
  }
  if (dte === 0) {
    return (
      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
        0 DTE — today
      </span>
    );
  }
  return (
    <span
      className={cn(
        'text-xs px-2 py-0.5 rounded-full',
        dte <= 7 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
      )}
    >
      {dte} DTE
    </span>
  );
}

/**
 * The whole card navigates on click/Enter/Space, but the container is a
 * `div[role="link"]`, not an `<a>` — SchwabLink is a real nested `<a
 * target="_blank">`, and an anchor inside an anchor is invalid HTML (React's
 * dev-mode DOM nesting validator flags it, and assistive tech sees two
 * overlapping interactive elements). Previously this was `<Link>` wrapping
 * everything, which produced exactly that nested-anchor DOM.
 */
export function RiskCard({
  row,
  onAskAi,
  onExplain,
}: {
  row: RiskRow;
  onAskAi: (positionId: string) => void;
  /** Per row, not per position — a position split across two dates has one Explain button per
   * row, and each must open its OWN row's dialog. `onAskAi` stays keyed by position: the AI
   * endpoint analyses a position, not a row. */
  onExplain: (rowKey: string) => void;
}) {
  const { safety } = row;
  const hasQuote = row.currentPrice !== null;
  const navigate = useNavigate();
  const goToChain = () => navigate(`/chains/${row.chainId}`);
  // Exactly one short side present: the chain's own label (e.g. "Iron Condor") describes a
  // structure this row no longer has, since a one-side roll split it into one-sided rows. Both
  // sides (a same-date merge) or neither (long-only) still match the chain's label, so only the
  // XOR case swaps in the row's own `rowStrategyLabel`.
  const isOneSided = Boolean(row.safety.put) !== Boolean(row.safety.call);

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={goToChain}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          goToChain();
        }
      }}
      className="block p-3 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-colors cursor-pointer"
    >
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-semibold text-gray-900 truncate">{row.symbol}</span>
          <SchwabLink href={schwabResearchUrl(row.symbol)} label={`${row.symbol} on Schwab`} ariaLabel={`Research ${row.symbol} on Schwab`} />
          <span data-testid="strategy-badge">
            {isOneSided ? (
              <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
                {row.rowStrategyLabel}
              </span>
            ) : (
              <StrategyBadge strategy={row.strategyType} size="sm" />
            )}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            data-testid="explain"
            onClick={(e) => {
              // The card itself navigates on click; without stopPropagation this would
              // open the dialog and leave the page at the same time.
              e.preventDefault();
              e.stopPropagation();
              onExplain(row.rowKey);
            }}
            className="rounded-md border border-gray-200 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
          >
            Explain
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onAskAi(row.positionId);
            }}
            className="rounded-md border border-gray-200 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
          >
            Ask AI
          </button>
          <span
            className={cn(
              'text-xs px-2 py-0.5 rounded-full',
              overallPillClasses[safety.overall]
            )}
          >
            {overallLabels[safety.overall]}
          </span>
        </div>
      </div>

      <div className="font-mono text-sm text-gray-900 mb-1 break-words">
        {safety.put && (
          <>
            <span>${formatStrike(safety.put.shortStrike)}P</span>{' '}
            <span className={sideTextClass(safety.put)}>
              ←{safety.put.percent !== null ? formatSidePercent(safety.put.percent) : '–'}
            </span>
            <span>· </span>
          </>
        )}
        <span>{hasQuote ? formatCurrency(row.currentPrice) : '$—'}</span>
        {safety.call && (
          <>
            <span> ·</span>
            <span className={sideTextClass(safety.call)}>
              {safety.call.percent !== null ? formatSidePercent(safety.call.percent) : '–'}→
            </span>{' '}
            <span>${formatStrike(safety.call.shortStrike)}C</span>
          </>
        )}
      </div>

      <div className="mb-1.5">
        <PayoffBar
          longPut={row.learnCtx.longPut}
          shortPut={row.safety.put?.shortStrike ?? null}
          shortCall={row.safety.call?.shortStrike ?? null}
          longCall={row.learnCtx.longCall}
          price={row.currentPrice}
          credit={row.learnCtx.credit}
          odds={row.odds}
          expectedMove={row.expectedMove}
        />
      </div>

      <div className="text-xs text-gray-500 flex items-center gap-2">
        <span className={row.netPremium >= 0 ? 'text-green-600' : 'text-red-600'}>
          {formatCurrency(row.netPremium)}
        </span>
        {row.changePercent !== null && (
          <span>
            {row.changePercent >= 0 ? '+' : ''}
            {row.changePercent.toFixed(1)}% today
          </span>
        )}
        <span data-testid="held">
          {/* null is "nobody could price this", not zero held — an em dash, never $0. */}
          {row.held.held == null ? '—' : formatCurrency(row.held.held, { cents: false })}
          {row.held.kinds.length > 0 && ` · ${kindLabel(row.held.kinds)}`}
        </span>
        {row.held.flags.includes('uncovered_call') && (
          <span data-testid="held-uncovered" className="text-amber-700">
            uncovered
          </span>
        )}
      </div>
    </div>
  );
}
